/**
 * Workflow Chat Store
 *
 * Zustand store for AI-powered workflow chat conversation state.
 * Manages messages, conversation state, and workflow generation context.
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { v4 as uuidv4 } from 'uuid';

/**
 * Chat message roles
 */
export type MessageRole = 'user' | 'assistant' | 'system';

/**
 * Chat message interface
 */
export interface WorkflowChatMessage {
  id: string;
  content: string;
  role: MessageRole;
  timestamp: Date;
  isStreaming?: boolean;
  /** If assistant message, may contain workflow suggestions */
  workflowSuggestion?: WorkflowSuggestion;
  /** Error message if the request failed */
  error?: string;
  /** Metadata for tracking */
  metadata?: Record<string, unknown>;
}

/**
 * Suggested node to add to the workflow
 */
export interface SuggestedNode {
  type: string;
  name: string;
  /** Optional human-readable node description from AI suggestion */
  description?: string;
  config?: Record<string, unknown>;
  /** Position hint for layout */
  positionHint?: 'after' | 'before' | 'parallel';
  /** ID of node to insert after (for precise insertion) */
  insertAfterNodeId?: string;
  /** ID of node to insert before (for precise insertion) */
  insertBeforeNodeId?: string;
}

/**
 * Suggested edge/connection between nodes
 */
export interface SuggestedEdge {
  sourceType: string;
  targetType: string;
  sourceHandle?: string;
  targetHandle?: string;
  /** Specific source node ID (for precise edge creation) */
  sourceNodeId?: string;
  /** Specific target node ID (for precise edge creation) - use '__NEW_NODE__' placeholder for new nodes */
  targetNodeId?: string;
}

/**
 * Modification to an existing node (rename, reconfigure)
 */
export interface NodeModification {
  /** ID of the existing node to modify */
  nodeId: string;
  /** New label/display name (optional) */
  newLabel?: string;
  /** Config properties to update (merged with existing) */
  configUpdates?: Record<string, unknown>;
  /** Description of what's being changed (for UI display) */
  changeDescription: string;
}

/**
 * Workflow suggestion from AI
 */
export interface WorkflowSuggestion {
  /** Human-readable description of the suggestion */
  description: string;
  /** Nodes to add */
  nodes: SuggestedNode[];
  /** Modifications to existing nodes (rename, update config) */
  nodeModifications?: NodeModification[];
  /** Edges to add (connections) */
  edges?: SuggestedEdge[];
  /** Edge IDs to remove (when inserting a node between existing nodes) */
  edgesToRemove?: string[];
  /** Whether this is a complete workflow or partial */
  isComplete: boolean;
  /** If AI needs clarification */
  clarificationQuestion?: string;
  /** Suggested options for clarification */
  clarificationOptions?: string[];
}

/**
 * Conversation summary for listing
 */
export interface WorkflowConversationSummary {
  id: string;
  title: string;
  workflowId?: string;
  lastMessage?: string;
  updatedAt: Date;
  messageCount: number;
}

/**
 * Full conversation with messages
 */
export interface WorkflowConversation {
  id: string;
  title: string;
  workflowId?: string;
  workflowName?: string;
  messages: WorkflowChatMessage[];
  createdAt: Date;
  updatedAt: Date;
  status: 'active' | 'archived';
}

/**
 * Workflow chat store state
 */
interface WorkflowChatState {
  // Conversation list
  conversations: WorkflowConversationSummary[];
  isLoadingConversations: boolean;

  // Active conversation
  activeConversationId: string | null;
  activeConversation: WorkflowConversation | null;
  messages: WorkflowChatMessage[];

  // UI state
  inputValue: string;
  isTyping: boolean;
  isLoading: boolean;
  error: string | null;
  showConversationList: boolean;

  // Workflow context
  currentWorkflowId: string | null;
  currentWorkflowName: string | null;

  // Pending suggestion awaiting user approval
  pendingSuggestion: WorkflowSuggestion | null;

  // Actions
  setInputValue: (value: string) => void;
  setError: (error: string | null) => void;
  setIsTyping: (isTyping: boolean) => void;
  setIsLoading: (isLoading: boolean) => void;
  toggleConversationList: () => void;

  // Message actions
  addMessage: (message: Omit<WorkflowChatMessage, 'id' | 'timestamp'>) => WorkflowChatMessage;
  updateMessage: (id: string, updates: Partial<WorkflowChatMessage>) => void;
  removeMessage: (id: string) => void;
  clearMessages: () => void;

  // Conversation actions
  loadConversations: (workflowId?: string) => Promise<void>;
  createConversation: (title?: string, workflowId?: string) => Promise<string>;
  switchConversation: (id: string) => Promise<void>;
  clearActiveConversation: () => void;
  deleteConversation: (id: string) => Promise<void>;

  // Workflow context actions
  setWorkflowContext: (workflowId: string | null, workflowName: string | null) => void;

  // Suggestion actions
  setPendingSuggestion: (suggestion: WorkflowSuggestion | null) => void;
  acceptSuggestion: () => WorkflowSuggestion | null;
  rejectSuggestion: () => void;

  // Initialization
  initialize: (workflowId?: string) => Promise<void>;
  reset: () => void;
}

/**
 * Initial state for the store
 */
const initialState = {
  // Conversation list
  conversations: [] as WorkflowConversationSummary[],
  isLoadingConversations: false,

  // Active conversation
  activeConversationId: null as string | null,
  activeConversation: null as WorkflowConversation | null,
  messages: [] as WorkflowChatMessage[],

  // UI state
  inputValue: '',
  isTyping: false,
  isLoading: false,
  error: null as string | null,
  showConversationList: false,

  // Workflow context
  currentWorkflowId: null as string | null,
  currentWorkflowName: null as string | null,

  // Pending suggestion
  pendingSuggestion: null as WorkflowSuggestion | null,
};

/**
 * Workflow Chat Store
 */
export const useWorkflowChatStore = create<WorkflowChatState>()(
  immer((set, get) => ({
    ...initialState,

    // UI Actions
    setInputValue: (value: string) => {
      set((state) => {
        state.inputValue = value;
      });
    },

    setError: (error: string | null) => {
      set((state) => {
        state.error = error;
      });
    },

    setIsTyping: (isTyping: boolean) => {
      set((state) => {
        state.isTyping = isTyping;
      });
    },

    setIsLoading: (isLoading: boolean) => {
      set((state) => {
        state.isLoading = isLoading;
      });
    },

    toggleConversationList: () => {
      set((state) => {
        state.showConversationList = !state.showConversationList;
      });
    },

    // Message Actions
    addMessage: (message: Omit<WorkflowChatMessage, 'id' | 'timestamp'>) => {
      const newMessage: WorkflowChatMessage = {
        ...message,
        id: uuidv4(),
        timestamp: new Date(),
      };

      set((state) => {
        state.messages.push(newMessage);
        state.error = null;
      });

      return newMessage;
    },

    updateMessage: (id: string, updates: Partial<WorkflowChatMessage>) => {
      set((state) => {
        const index = state.messages.findIndex((m) => m.id === id);
        if (index !== -1) {
          state.messages[index] = { ...state.messages[index], ...updates };
        }
      });
    },

    removeMessage: (id: string) => {
      set((state) => {
        state.messages = state.messages.filter((m) => m.id !== id);
      });
    },

    clearMessages: () => {
      set((state) => {
        state.messages = [];
        state.pendingSuggestion = null;
      });
    },

    // Conversation Actions
    loadConversations: async (workflowId?: string) => {
      set((state) => {
        state.isLoadingConversations = true;
      });

      try {
        // TODO: Call workflow chat API when backend is ready
        // For now, use empty list
        set((state) => {
          state.conversations = [];
          state.isLoadingConversations = false;
        });
      } catch (error) {
        console.error('Failed to load conversations:', error);
        set((state) => {
          state.isLoadingConversations = false;
          state.error = 'Failed to load conversations';
        });
      }
    },

    createConversation: async (title?: string, workflowId?: string) => {
      const conversationId = uuidv4();
      const now = new Date();

      const newConversation: WorkflowConversation = {
        id: conversationId,
        title: title || 'New Workflow Chat',
        workflowId,
        messages: [],
        createdAt: now,
        updatedAt: now,
        status: 'active',
      };

      set((state) => {
        state.activeConversationId = conversationId;
        state.activeConversation = newConversation;
        state.messages = [];
        state.conversations.unshift({
          id: conversationId,
          title: newConversation.title,
          workflowId,
          updatedAt: now,
          messageCount: 0,
        });
      });

      // TODO: Save to backend when API is ready
      return conversationId;
    },

    switchConversation: async (id: string) => {
      set((state) => {
        state.isLoading = true;
      });

      try {
        // TODO: Load conversation from backend when API is ready
        // For now, just set the active ID
        set((state) => {
          state.activeConversationId = id;
          state.isLoading = false;
        });
      } catch (error) {
        console.error('Failed to switch conversation:', error);
        set((state) => {
          state.isLoading = false;
          state.error = 'Failed to load conversation';
        });
      }
    },

    clearActiveConversation: () => {
      set((state) => {
        state.activeConversationId = null;
        state.activeConversation = null;
        state.messages = [];
        state.pendingSuggestion = null;
      });
    },

    deleteConversation: async (id: string) => {
      try {
        // TODO: Delete from backend when API is ready
        set((state) => {
          state.conversations = state.conversations.filter((c) => c.id !== id);
          if (state.activeConversationId === id) {
            state.activeConversationId = null;
            state.activeConversation = null;
            state.messages = [];
          }
        });
      } catch (error) {
        console.error('Failed to delete conversation:', error);
        set((state) => {
          state.error = 'Failed to delete conversation';
        });
      }
    },

    // Workflow Context Actions
    setWorkflowContext: (workflowId: string | null, workflowName: string | null) => {
      set((state) => {
        state.currentWorkflowId = workflowId;
        state.currentWorkflowName = workflowName;
      });
    },

    // Suggestion Actions
    setPendingSuggestion: (suggestion: WorkflowSuggestion | null) => {
      set((state) => {
        state.pendingSuggestion = suggestion;
      });
    },

    acceptSuggestion: () => {
      const state = get();
      const suggestion = state.pendingSuggestion;

      set((draft) => {
        draft.pendingSuggestion = null;
      });

      return suggestion;
    },

    rejectSuggestion: () => {
      set((state) => {
        state.pendingSuggestion = null;
      });

      // Add a message indicating rejection
      get().addMessage({
        content: 'Suggestion rejected. Please describe what you\'d like instead.',
        role: 'assistant',
      });
    },

    // Initialization
    initialize: async (workflowId?: string) => {
      if (workflowId) {
        get().setWorkflowContext(workflowId, null);
      }
      await get().loadConversations(workflowId);

      // Add welcome message
      set((state) => {
        if (state.messages.length === 0) {
          state.messages.push({
            id: uuidv4(),
            content: 'Hello! I can help you build workflows. Describe what you want your workflow to do, and I\'ll suggest the nodes to create it.\n\nFor example:\n- "Create a workflow that sends an email when data is received"\n- "Add a delay of 5 minutes before the notification"\n- "Build a workflow to process sensor data and store it"',
            role: 'assistant',
            timestamp: new Date(),
          });
        }
      });
    },

    reset: () => {
      set(initialState);
    },
  }))
);

/**
 * Selector hooks for common use cases
 */
export const useWorkflowChatMessages = () => useWorkflowChatStore((state) => state.messages);
export const useWorkflowChatInput = () => useWorkflowChatStore((state) => state.inputValue);
export const useWorkflowChatLoading = () => useWorkflowChatStore((state) => state.isLoading);
export const useWorkflowChatError = () => useWorkflowChatStore((state) => state.error);
export const usePendingSuggestion = () => useWorkflowChatStore((state) => state.pendingSuggestion);
